import cases from './stays-template-placeholders.cases.json';

/**
 * LOBBERO STAYS — the details an alert or a message template may use
 * (ai-plans/stays plan S8, R-S8.11 / R-S8.21). The lists are the SERVER's,
 * read from stays-template-placeholders.cases.json (a COPY generated from
 * lobbero_be/src/stays/alerts/templates.js and src/stays/messages/placeholders.js
 * by make-s8-cases.js; check-fe-copy.js keeps it current). The server decides
 * every save; the editor uses these to offer the "Insert a detail" chips and
 * to warn early with the server's own codes.
 */

export type AlertTemplateKind = 'guest_arrived' | 'cleaning_scheduled' | 'cleaning_unscheduled' | 'turnaround';

export const ALERT_TEMPLATE_KINDS = cases.alert_kinds as AlertTemplateKind[];
export const ALERT_PLACEHOLDERS = cases.alert_placeholders as Record<AlertTemplateKind, string[]>;
export const MESSAGE_PLACEHOLDERS: string[] = cases.message_placeholders;
export const SENSITIVE_PLACEHOLDERS: string[] = cases.sensitive_placeholders;
export const ALERT_TEMPLATE_MAX: number = cases.alert_max;
export const MESSAGE_MAX: number = cases.message_max;

const TOKEN = /\{([a-zA-Z0-9]+)\}/g;
// Control characters other than a newline and a tab (the server's rule).
// eslint-disable-next-line no-control-regex
const BAD_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/;

export const tokensOf = (text: string): string[] => {
  const out: string[] = [];
  const re = new RegExp(TOKEN.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(String(text))) !== null) out.push(m[1]);

  return out;
};

export interface TemplateVerdict {
  code: 'TEMPLATE_EMPTY' | 'TEMPLATE_TOO_LONG' | 'TEMPLATE_INVALID_CHARACTERS' | 'TEMPLATE_PLACEHOLDER_UNKNOWN';
  placeholders: string[];
}

const verdict = (text: string, allowed: string[], max: number, maxLines: number): TemplateVerdict | null => {
  if (typeof text !== 'string' || !text.trim()) return { code: 'TEMPLATE_EMPTY', placeholders: [] };
  if (text.length > max) return { code: 'TEMPLATE_TOO_LONG', placeholders: [] };
  if (BAD_CHARS.test(text) || text.split('\n').length > maxLines) return { code: 'TEMPLATE_INVALID_CHARACTERS', placeholders: [] };
  const unknown = Array.from(new Set(tokensOf(text).filter(t => !allowed.includes(t))));

  return unknown.length ? { code: 'TEMPLATE_PLACEHOLDER_UNKNOWN', placeholders: unknown } : null;
};

/** An alert template (≤ 1500 characters, 30 lines, that kind's details). */
export const checkAlertTemplate = (kind: AlertTemplateKind, text: string): TemplateVerdict | null =>
  verdict(text, ALERT_PLACEHOLDERS[kind] || [], ALERT_TEMPLATE_MAX, 30);

/** A message template (≤ 2000 characters, 60 lines, the message details incl. the sensitive ones). */
export const checkMessageTemplate = (text: string): TemplateVerdict | null =>
  verdict(text, [...MESSAGE_PLACEHOLDERS, ...SENSITIVE_PLACEHOLDERS], MESSAGE_MAX, 60);

/** Insert `{name}` at the caret of a text, → the new text and caret. */
export const insertPlaceholder = (
  text: string,
  name: string,
  start: number | null | undefined,
  end: number | null | undefined
): { text: string; caret: number } => {
  const token = `{${name}}`;
  const a = typeof start === 'number' ? Math.max(0, Math.min(start, text.length)) : text.length;
  const b = typeof end === 'number' ? Math.max(a, Math.min(end, text.length)) : a;

  return { text: `${text.slice(0, a)}${token}${text.slice(b)}`, caret: a + token.length };
};
